import React, { useState } from 'react';
import {
  Drawer,
  Box,
  Typography,
  IconButton,
  TextField,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Collapse,
  Chip,
  Button,
  Divider,
  Badge,
  Menu,
  MenuItem,
  useMediaQuery,
  ThemeProvider,
  createTheme,
  CircularProgress,
  Alert
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import TaskAltIcon from '@mui/icons-material/TaskAlt';
import AddIcon from '@mui/icons-material/Add';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import StarOutlineIcon from '@mui/icons-material/StarOutline';
import StarIcon from '@mui/icons-material/Star';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import PlaylistAddCheckIcon from '@mui/icons-material/PlaylistAddCheck';
import NotesIcon from '@mui/icons-material/Notes';
import StickyNote2OutlinedIcon from '@mui/icons-material/StickyNote2Outlined';
import BarChartIcon from '@mui/icons-material/BarChart';
import PeopleOutlineIcon from '@mui/icons-material/PeopleOutline';
import NotificationsNoneIcon from '@mui/icons-material/NotificationsNone';
import TodayIcon from '@mui/icons-material/Today';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ShareOutlinedIcon from '@mui/icons-material/ShareOutlined';
import { useTarefas, formatDataBr } from '../../hooks/useTarefas';
import BlocoNotasView from './BlocoNotasView';
import TarefasIndicadoresView from './TarefasIndicadoresView';
import CompartilharDialog from './CompartilharDialog';

const tasksTheme = createTheme({
  palette: {
    mode: 'dark',
    primary: { main: '#8ab4f8' },
    background: { default: '#202124', paper: '#292a2d' }
  },
  typography: { fontFamily: '"Google Sans", "Roboto", "Helvetica", "Arial", sans-serif' },
  shape: { borderRadius: 12 }
});

const SIDEBAR_W = 256;

function NavItem({ active, icon, label, onClick }) {
  return (
    <ListItemButton
      onClick={onClick}
      sx={{
        borderRadius: '999px',
        mx: 1,
        mb: 0.25,
        py: 0.75,
        bgcolor: active ? 'rgba(138,180,248,0.18)' : 'transparent',
        color: active ? 'primary.main' : 'text.primary',
        '&:hover': { bgcolor: active ? 'rgba(138,180,248,0.22)' : 'rgba(255,255,255,0.06)' }
      }}
    >
      <ListItemIcon sx={{ minWidth: 36, color: 'inherit' }}>{icon}</ListItemIcon>
      <ListItemText primary={label} primaryTypographyProps={{ fontSize: '0.875rem', fontWeight: active ? 600 : 400 }} />
    </ListItemButton>
  );
}

function TaskRow({
  tarefa,
  expanded,
  onExpand,
  onToggle,
  onStar,
  onDelete,
  onAddSub,
  onToggleSub,
  onDeleteSub,
  souDono,
  podeEditar,
  podeCompartilhar,
  onOpenShare
}) {
  const [subText, setSubText] = useState('');
  const subs = tarefa.subtarefas || [];
  const temSubs = subs.length > 0;
  const concluidasSubs = subs.filter((s) => s.concluida).length;
  const toggleDisabled = !podeEditar || (temSubs && !tarefa.concluida);

  return (
    <Box sx={{ borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: 1,
          py: 1.25,
          px: 0.5,
          '&:hover .task-actions': { opacity: 1 }
        }}
      >
        <IconButton
          size="small"
          onClick={() => !toggleDisabled && onToggle(tarefa)}
          disabled={toggleDisabled}
          sx={{ mt: 0.25, color: 'text.secondary' }}
          title={toggleDisabled ? 'Conclua todas as subtarefas' : undefined}
        >
          {tarefa.concluida ? (
            <CheckCircleIcon sx={{ color: 'primary.main', fontSize: 22 }} />
          ) : (
            <RadioButtonUncheckedIcon sx={{ fontSize: 22 }} />
          )}
        </IconButton>
        <Box sx={{ flex: 1, minWidth: 0 }} onClick={() => onExpand(tarefa.id)} sx={{ cursor: 'pointer' }}>
          <Typography
            sx={{
              fontSize: '0.95rem',
              textDecoration: tarefa.concluida ? 'line-through' : 'none',
              color: tarefa.concluida ? 'text.secondary' : 'text.primary',
              wordBreak: 'break-word'
            }}
          >
            {tarefa.titulo}
          </Typography>
          {tarefa.detalhes && (
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.25 }}>
              {tarefa.detalhes}
            </Typography>
          )}
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.75 }}>
            <Typography variant="caption" color="text.secondary">
              Criada: {formatDataBr(tarefa.createdAt)}
            </Typography>
            {tarefa.concluidaEm && (
              <Typography variant="caption" color="text.secondary">
                · Finalizada: {formatDataBr(tarefa.concluidaEm)}
              </Typography>
            )}
            {temSubs && (
              <Chip
                size="small"
                label={`${concluidasSubs}/${subs.length} subtarefas`}
                sx={{ height: 20, fontSize: '0.65rem' }}
              />
            )}
            {tarefa.dataVencimento && (
              <Chip
                size="small"
                label={formatDataBr(tarefa.dataVencimento)}
                sx={{ height: 20, fontSize: '0.65rem', bgcolor: 'rgba(255,255,255,0.08)' }}
              />
            )}
            {tarefa.compartilhada && tarefa.dono && (
              <Chip size="small" label={`De: ${tarefa.dono.nome}`} sx={{ height: 20, fontSize: '0.65rem' }} />
            )}
            {tarefa.compartilhada && (
              <Chip
                size="small"
                label={tarefa.minhaPermissao === 'editar' ? 'Pode editar' : 'Somente leitura'}
                sx={{ height: 20, fontSize: '0.65rem', bgcolor: 'rgba(138,180,248,0.15)' }}
              />
            )}
          </Box>
        </Box>
        <Box className="task-actions" sx={{ display: 'flex', opacity: 0, transition: 'opacity 0.15s' }}>
          <IconButton size="small" onClick={() => onExpand(tarefa.id)} sx={{ color: 'text.secondary' }}>
            {expanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
          </IconButton>
          {souDono && (
            <IconButton size="small" onClick={() => onStar(tarefa.id)} sx={{ color: tarefa.estrelada ? '#fdd663' : 'text.secondary' }}>
              {tarefa.estrelada ? <StarIcon fontSize="small" /> : <StarOutlineIcon fontSize="small" />}
            </IconButton>
          )}
          {souDono && podeCompartilhar && (
            <IconButton size="small" onClick={() => onOpenShare(tarefa)} sx={{ color: 'text.secondary' }} title="Compartilhar">
              <ShareOutlinedIcon fontSize="small" />
            </IconButton>
          )}
          {souDono && (
            <IconButton size="small" onClick={() => onDelete(tarefa.id)} sx={{ color: 'text.secondary' }}>
              <DeleteOutlineIcon fontSize="small" />
            </IconButton>
          )}
        </Box>
      </Box>

      <Collapse in={expanded}>
        <Box sx={{ pl: 5, pr: 1, pb: 1.5 }}>
          {subs.map((sub) => (
            <Box key={sub.id} sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5, py: 0.5 }}>
              <IconButton
                size="small"
                onClick={() => podeEditar && onToggleSub(tarefa.id, sub.id, !sub.concluida)}
                disabled={!podeEditar}
                sx={{ color: 'text.secondary', mt: 0.1 }}
              >
                {sub.concluida ? (
                  <CheckCircleIcon sx={{ fontSize: 18, color: 'primary.main' }} />
                ) : (
                  <RadioButtonUncheckedIcon sx={{ fontSize: 18 }} />
                )}
              </IconButton>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography
                  variant="body2"
                  sx={{
                    textDecoration: sub.concluida ? 'line-through' : 'none',
                    color: sub.concluida ? 'text.secondary' : 'text.primary'
                  }}
                >
                  {sub.titulo}
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block">
                  <span>Criada: {formatDataBr(sub.createdAt)}</span>
                  {sub.concluidaEm && (
                    <span>{` · Finalizada: ${formatDataBr(sub.concluidaEm)}`}</span>
                  )}
                </Typography>
              </Box>
              {souDono && (
                <IconButton size="small" onClick={() => onDeleteSub(tarefa.id, sub.id)} sx={{ color: 'text.secondary' }}>
                  <DeleteOutlineIcon sx={{ fontSize: 16 }} />
                </IconButton>
              )}
            </Box>
          ))}
          {podeEditar && (
            <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5, alignItems: 'center' }}>
              <AddIcon sx={{ fontSize: 16, color: 'text.secondary', ml: 0.5 }} />
              <TextField
                size="small"
                fullWidth
                placeholder="Adicionar subtarefa"
                variant="standard"
                value={subText}
                onChange={(e) => setSubText(e.target.value)}
                onKeyDown={async (e) => {
                  if (e.key === 'Enter' && subText.trim()) {
                    await onAddSub(tarefa.id, subText);
                    setSubText('');
                  }
                }}
                InputProps={{ sx: { fontSize: '0.85rem' } }}
              />
            </Box>
          )}
        </Box>
      </Collapse>
    </Box>
  );
}

export default function TarefasPanel({ open, onClose, modo = 'isg' }) {
  const isMobile = useMediaQuery('(max-width:720px)');
  const [sidebarOpen, setSidebarOpen] = useState(!isMobile);
  const [adding, setAdding] = useState(false);
  const [titulo, setTitulo] = useState('');
  const [detalhes, setDetalhes] = useState('');
  const [dataVencimento, setDataVencimento] = useState(null);
  const [concluidasOpen, setConcluidasOpen] = useState(false);
  const [listasOpen, setListasOpen] = useState(true);
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [criandoLista, setCriandoLista] = useState(false);
  const [nomeNovaLista, setNomeNovaLista] = useState('');
  const [expandedId, setExpandedId] = useState(null);
  const [shareTarefa, setShareTarefa] = useState(null);
  const [notifAnchor, setNotifAnchor] = useState(null);

  const {
    loading,
    loadError,
    refresh,
    pendentes,
    concluidas,
    concluidasCount,
    tarefasProprias,
    listas,
    listaAtiva,
    listaAtivaId,
    setListaAtivaId,
    filtroNav,
    setFiltroNav,
    viewMode,
    setViewMode,
    notasFiltradas,
    tags,
    buscaNotas,
    setBuscaNotas,
    filtroTagsNotas,
    setFiltroTagsNotas,
    toggleFiltroTagNota,
    notaAtivaId,
    setNotaAtivaId,
    notificacoes,
    podeCompartilhar,
    adicionar,
    concluir,
    reabrir,
    toggleEstrela,
    excluir,
    criarLista,
    definirVencimento,
    adicionarSubtarefa,
    toggleSubtarefa,
    excluirSubtarefa,
    adicionarNota,
    atualizarNota,
    excluirNota,
    criarTag,
    carregarIndicadores,
    listarUsuariosCompartilhaveis,
    compartilharNota,
    atualizarCompartilhamentoNota,
    removerCompartilhamentoNota,
    compartilharTarefa,
    atualizarCompartilhamentoTarefa,
    removerCompartilhamentoTarefa,
    marcarNotificacaoLida,
    marcarTodasNotificacoesLidas
  } = useTarefas(modo, { enabled: open });

  const tituloLista =
    viewMode === 'indicadores'
      ? 'Indicadores'
      : viewMode === 'notas'
        ? filtroNav === 'compartilhadas'
          ? 'Notas compartilhadas comigo'
          : 'Bloco de notas'
        : filtroNav === 'compartilhadas'
          ? 'Tarefas compartilhadas comigo'
          : filtroNav === 'estrela'
            ? 'Com estrela'
            : filtroNav === 'todas'
              ? 'Todas as tarefas'
              : listaAtiva?.nome || 'Minhas tarefas';

  const resetForm = () => {
    setTitulo('');
    setDetalhes('');
    setDataVencimento(null);
    setAdding(false);
  };

  const handleSalvar = async () => {
    if (await adicionar({ titulo, detalhes, dataVencimento, listaId: listaAtivaId })) resetForm();
  };

  const handleToggle = (tarefa) => {
    if (tarefa.concluida) reabrir(tarefa.id);
    else concluir(tarefa.id);
  };

  const abrirAdicionar = () => {
    setViewMode('tarefas');
    setAdding(true);
    if (isMobile) setSidebarOpen(false);
  };

  const selecionarNav = (tipo, listaId) => {
    setViewMode('tarefas');
    setFiltroNav(tipo);
    if (listaId) setListaAtivaId(listaId);
    if (isMobile) setSidebarOpen(false);
  };

  const abrirNotas = () => {
    setViewMode('notas');
    if (filtroNav === 'compartilhadas') setFiltroNav('todas');
    if (isMobile) setSidebarOpen(false);
  };

  const abrirIndicadores = () => {
    setViewMode('indicadores');
    if (isMobile) setSidebarOpen(false);
  };

  const abrirCompartilhadas = () => {
    setFiltroNav('compartilhadas');
    if (viewMode === 'indicadores') setViewMode('tarefas');
    if (isMobile) setSidebarOpen(false);
  };

  const handleNotificacaoClick = async (n) => {
    await marcarNotificacaoLida(n.id);
    setFiltroNav('compartilhadas');
    if (n.itemTipo === 'nota') {
      setViewMode('notas');
      setNotaAtivaId(n.itemId);
    } else {
      setViewMode('tarefas');
      setExpandedId(n.itemId);
    }
    setNotifAnchor(null);
    if (isMobile) setSidebarOpen(false);
  };

  const taskRowProps = (t) => {
    const souDono = t.souDono !== false;
    const podeEditar = souDono || t.minhaPermissao === 'editar';
    return {
      tarefa: t,
      expanded: expandedId === t.id,
      onExpand: (id) => setExpandedId((prev) => (prev === id ? null : id)),
      onToggle: handleToggle,
      onStar: toggleEstrela,
      onDelete: excluir,
      onAddSub: adicionarSubtarefa,
      onToggleSub: toggleSubtarefa,
      onDeleteSub: excluirSubtarefa,
      souDono,
      podeEditar,
      podeCompartilhar,
      onOpenShare: (tarefa) => setShareTarefa(tarefa)
    };
  };

  if (!open) return null;

  return (
    <ThemeProvider theme={tasksTheme}>
      <Drawer
        anchor="right"
        open={open}
        onClose={onClose}
        PaperProps={{
          sx: {
            width: isMobile ? '100%' : '50vw',
            minWidth: isMobile ? undefined : 480,
            maxWidth: '100vw',
            bgcolor: 'background.default',
            color: 'text.primary'
          }
        }}
      >
        <Box sx={{ display: 'flex', height: '100%', overflow: 'hidden' }}>
          {(!isMobile || sidebarOpen) && (
            <Box
              sx={{
                width: isMobile ? '100%' : SIDEBAR_W,
                flexShrink: 0,
                borderRight: isMobile ? 0 : '1px solid rgba(255,255,255,0.08)',
                display: 'flex',
                flexDirection: 'column',
                bgcolor: 'background.default'
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 2, py: 2 }}>
                {isMobile && (
                  <IconButton onClick={() => setSidebarOpen(false)} sx={{ color: 'text.primary' }}>
                    <CloseIcon />
                  </IconButton>
                )}
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    borderRadius: '50%',
                    bgcolor: 'primary.main',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#202124'
                  }}
                >
                  <TaskAltIcon />
                </Box>
                <Typography variant="h6" fontWeight={400}>
                  Tarefas
                </Typography>
              </Box>

              <Box sx={{ px: 2, mb: 1 }}>
                <Button
                  fullWidth
                  startIcon={<AddIcon />}
                  onClick={abrirAdicionar}
                  sx={{
                    justifyContent: 'flex-start',
                    textTransform: 'none',
                    borderRadius: '999px',
                    bgcolor: 'rgba(255,255,255,0.08)',
                    color: 'text.primary',
                    py: 1,
                    '&:hover': { bgcolor: 'rgba(255,255,255,0.12)' }
                  }}
                >
                  Criar
                </Button>
              </Box>

              <List dense disablePadding sx={{ mb: 1 }}>
                <NavItem
                  active={viewMode === 'tarefas' && filtroNav === 'todas'}
                  icon={<CheckCircleOutlineIcon fontSize="small" />}
                  label="Todas as tarefas"
                  onClick={() => selecionarNav('todas', 'all')}
                />
                <NavItem
                  active={viewMode === 'tarefas' && filtroNav === 'estrela'}
                  icon={<StarOutlineIcon fontSize="small" />}
                  label="Com estrela"
                  onClick={() => selecionarNav('estrela')}
                />
                <NavItem
                  active={viewMode === 'notas'}
                  icon={<StickyNote2OutlinedIcon fontSize="small" />}
                  label="Bloco de notas"
                  onClick={abrirNotas}
                />
                <NavItem
                  active={viewMode === 'indicadores'}
                  icon={<BarChartIcon fontSize="small" />}
                  label="Indicadores"
                  onClick={abrirIndicadores}
                />
                {podeCompartilhar && (
                  <NavItem
                    active={filtroNav === 'compartilhadas'}
                    icon={<PeopleOutlineIcon fontSize="small" />}
                    label="Compartilhadas comigo"
                    onClick={abrirCompartilhadas}
                  />
                )}
              </List>

              <Divider sx={{ borderColor: 'rgba(255,255,255,0.08)', mx: 2 }} />

              <ListItemButton onClick={() => setListasOpen((v) => !v)} sx={{ py: 1, px: 2 }}>
                <ListItemText
                  primary={viewMode === 'notas' ? 'Tags' : 'Listas'}
                  primaryTypographyProps={{ fontSize: '0.8rem', color: 'text.secondary' }}
                />
                {listasOpen ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
              </ListItemButton>

              <Collapse in={listasOpen}>
                <List dense disablePadding>
                  {viewMode === 'notas' ? (
                    <>
                      {tags.length === 0 ? (
                        <Typography variant="caption" color="text.secondary" sx={{ px: 2, py: 1 }}>
                          Crie tags ao editar uma nota
                        </Typography>
                      ) : (
                        tags.map((tag) => (
                          <ListItemButton
                            key={tag.id}
                            onClick={() => toggleFiltroTagNota(tag.id)}
                            sx={{
                              borderRadius: '999px',
                              mx: 1,
                              mb: 0.25,
                              bgcolor: filtroTagsNotas.includes(tag.id)
                                ? 'rgba(138,180,248,0.18)'
                                : 'transparent'
                            }}
                          >
                            <ListItemIcon sx={{ minWidth: 28 }}>
                              <Box
                                sx={{
                                  width: 10,
                                  height: 10,
                                  borderRadius: '50%',
                                  bgcolor: tag.cor
                                }}
                              />
                            </ListItemIcon>
                            <ListItemText
                              primary={tag.nome}
                              secondary={`${tag.quantidadeNotas || 0} nota(s)`}
                              primaryTypographyProps={{ fontSize: '0.85rem' }}
                              secondaryTypographyProps={{ fontSize: '0.65rem' }}
                            />
                          </ListItemButton>
                        ))
                      )}
                      {filtroTagsNotas.length > 0 && (
                        <ListItemButton
                          onClick={() => setFiltroTagsNotas([])}
                          sx={{ mx: 1 }}
                        >
                          <ListItemText
                            primary="Limpar filtros de tag"
                            primaryTypographyProps={{ fontSize: '0.75rem', color: 'primary.main' }}
                          />
                        </ListItemButton>
                      )}
                    </>
                  ) : (
                    <>
                      {listas.map((lista) => (
                    <NavItem
                      key={lista.id}
                      active={viewMode === 'tarefas' && filtroNav === 'lista' && listaAtivaId === lista.id}
                      icon={<PlaylistAddCheckIcon fontSize="small" />}
                      label={lista.nome}
                      onClick={() => selecionarNav('lista', lista.id)}
                    />
                  ))}
                  {criandoLista ? (
                    <Box sx={{ px: 2, py: 1 }}>
                      <TextField
                        autoFocus
                        size="small"
                        fullWidth
                        placeholder="Nome da lista"
                        value={nomeNovaLista}
                        onChange={(e) => setNomeNovaLista(e.target.value)}
                        onKeyDown={async (e) => {
                          if (e.key === 'Enter' && (await criarLista(nomeNovaLista))) {
                            setNomeNovaLista('');
                            setCriandoLista(false);
                          }
                          if (e.key === 'Escape') setCriandoLista(false);
                        }}
                        variant="standard"
                        InputProps={{ sx: { fontSize: '0.875rem', color: 'text.primary' } }}
                      />
                    </Box>
                  ) : (
                    <ListItemButton onClick={() => setCriandoLista(true)} sx={{ mx: 1, borderRadius: '999px' }}>
                      <ListItemIcon sx={{ minWidth: 36 }}>
                        <AddIcon fontSize="small" />
                      </ListItemIcon>
                      <ListItemText primary="Criar nova lista" primaryTypographyProps={{ fontSize: '0.875rem' }} />
                    </ListItemButton>
                  )}
                    </>
                  )}
                </List>
              </Collapse>
            </Box>
          )}

          {(!isMobile || !sidebarOpen) && (
            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0, overflow: 'hidden' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', px: 1, py: 0.5, flexShrink: 0 }}>
                {isMobile && (
                  <IconButton onClick={() => setSidebarOpen(true)} sx={{ mr: 'auto', color: 'text.primary' }}>
                    <ExpandMoreIcon sx={{ transform: 'rotate(90deg)' }} />
                  </IconButton>
                )}
                {podeCompartilhar && (
                  <IconButton onClick={(e) => setNotifAnchor(e.currentTarget)} sx={{ color: 'text.secondary' }}>
                    <Badge badgeContent={notificacoes?.totalNaoLidas || 0} color="primary">
                      <NotificationsNoneIcon />
                    </Badge>
                  </IconButton>
                )}
                <IconButton onClick={onClose} aria-label="Fechar" sx={{ color: 'text.secondary' }}>
                  <CloseIcon />
                </IconButton>
              </Box>

              <Menu
                anchorEl={notifAnchor}
                open={Boolean(notifAnchor)}
                onClose={() => setNotifAnchor(null)}
                PaperProps={{ sx: { maxWidth: 360, maxHeight: 400 } }}
              >
                <Box sx={{ px: 2, py: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="subtitle2">Notificações</Typography>
                  {(notificacoes?.totalNaoLidas || 0) > 0 && (
                    <Button
                      size="small"
                      onClick={async () => {
                        await marcarTodasNotificacoesLidas();
                        setNotifAnchor(null);
                      }}
                      sx={{ textTransform: 'none' }}
                    >
                      Marcar todas lidas
                    </Button>
                  )}
                </Box>
                {(notificacoes?.items || []).length === 0 ? (
                  <MenuItem disabled>Nenhuma notificação</MenuItem>
                ) : (
                  (notificacoes?.items || []).map((n) => (
                    <MenuItem
                      key={n.id}
                      onClick={() => handleNotificacaoClick(n)}
                      sx={{ opacity: n.lida ? 0.65 : 1, alignItems: 'flex-start', whiteSpace: 'normal' }}
                    >
                      <ListItemText
                        primary={n.titulo}
                        secondary={n.mensagem}
                        primaryTypographyProps={{ fontSize: '0.85rem', fontWeight: n.lida ? 400 : 600 }}
                        secondaryTypographyProps={{ fontSize: '0.75rem' }}
                      />
                    </MenuItem>
                  ))
                )}
              </Menu>

              <Box sx={{ flex: 1, overflow: 'auto', px: { xs: 1.5, sm: 2 }, pb: 3 }}>
                <Box
                  sx={{
                    width: '100%',
                    bgcolor: 'background.paper',
                    borderRadius: 3,
                    border: '1px solid rgba(255,255,255,0.08)',
                    minHeight: 'calc(100vh - 120px)',
                    display: 'flex',
                    flexDirection: 'column'
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      px: 2.5,
                      py: 2,
                      borderBottom: '1px solid rgba(255,255,255,0.06)'
                    }}
                  >
                    <Typography variant="h6" fontWeight={400}>
                      {tituloLista}
                    </Typography>
                    {viewMode === 'tarefas' && filtroNav !== 'compartilhadas' && (
                      <IconButton size="small" onClick={(e) => setMenuAnchor(e.currentTarget)} sx={{ color: 'text.secondary' }}>
                        <MoreVertIcon />
                      </IconButton>
                    )}
                    <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={() => setMenuAnchor(null)}>
                      <MenuItem
                        onClick={async () => {
                          for (const t of concluidas) await excluir(t.id);
                          setMenuAnchor(null);
                        }}
                        disabled={concluidasCount === 0}
                      >
                        Excluir concluídas
                      </MenuItem>
                    </Menu>
                  </Box>

                  {loadError ? (
                    <Box sx={{ p: 2 }}>
                      <Alert
                        severity="warning"
                        action={
                          <Button color="inherit" size="small" onClick={refresh}>
                            Tentar novamente
                          </Button>
                        }
                      >
                        {loadError}
                      </Alert>
                    </Box>
                  ) : loading ? (
                    <Box display="flex" justifyContent="center" py={6}>
                      <CircularProgress />
                    </Box>
                  ) : viewMode === 'notas' ? (
                    <BlocoNotasView
                      notas={notasFiltradas}
                      todasTags={tags}
                      notaAtivaId={notaAtivaId}
                      setNotaAtivaId={setNotaAtivaId}
                      busca={buscaNotas}
                      onBuscaChange={setBuscaNotas}
                      onCriar={adicionarNota}
                      onSalvar={atualizarNota}
                      onExcluir={excluirNota}
                      onCriarTag={criarTag}
                      loading={false}
                      podeCompartilhar={podeCompartilhar}
                      listarUsuarios={listarUsuariosCompartilhaveis}
                      onCompartilhar={compartilharNota}
                      onAlterarCompartilhamento={atualizarCompartilhamentoNota}
                      onRemoverCompartilhamento={removerCompartilhamentoNota}
                      filtroCompartilhadas={filtroNav === 'compartilhadas'}
                    />
                  ) : viewMode === 'indicadores' ? (
                    <TarefasIndicadoresView carregarIndicadores={carregarIndicadores} />
                  ) : (
                    <>
                      <Box sx={{ px: 2, py: 1.5, flexShrink: 0 }}>
                        {filtroNav !== 'compartilhadas' && !adding ? (
                          <Button
                            startIcon={<AddIcon sx={{ color: 'primary.main' }} />}
                            onClick={() => setAdding(true)}
                            sx={{
                              textTransform: 'none',
                              color: 'primary.main',
                              fontWeight: 500,
                              justifyContent: 'flex-start',
                              px: 0.5
                            }}
                          >
                            Adicionar uma tarefa
                          </Button>
                        ) : filtroNav !== 'compartilhadas' && adding ? (
                          <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
                            <RadioButtonUncheckedIcon sx={{ mt: 1.25, color: 'text.secondary', fontSize: 22 }} />
                            <Box sx={{ flex: 1 }}>
                              <TextField
                                autoFocus
                                fullWidth
                                placeholder="Título"
                                variant="standard"
                                value={titulo}
                                onChange={(e) => setTitulo(e.target.value)}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter' && !e.shiftKey) {
                                    e.preventDefault();
                                    handleSalvar();
                                  }
                                  if (e.key === 'Escape') resetForm();
                                }}
                                InputProps={{ disableUnderline: true, sx: { fontSize: '1rem', color: 'text.primary' } }}
                              />
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 1.5, color: 'text.secondary' }}>
                                <NotesIcon sx={{ fontSize: 18 }} />
                                <TextField
                                  fullWidth
                                  placeholder="Detalhes"
                                  variant="standard"
                                  value={detalhes}
                                  onChange={(e) => setDetalhes(e.target.value)}
                                  InputProps={{ disableUnderline: true, sx: { fontSize: '0.875rem', color: 'text.secondary' } }}
                                />
                              </Box>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 2, flexWrap: 'wrap' }}>
                                <Chip
                                  size="small"
                                  icon={<TodayIcon sx={{ fontSize: '16px !important' }} />}
                                  label="Hoje"
                                  onClick={() => setDataVencimento(definirVencimento('hoje'))}
                                  variant={dataVencimento === definirVencimento('hoje') ? 'filled' : 'outlined'}
                                  sx={{ borderRadius: '999px', height: 28 }}
                                />
                                <Chip
                                  size="small"
                                  label="Amanhã"
                                  onClick={() => setDataVencimento(definirVencimento('amanha'))}
                                  variant={dataVencimento === definirVencimento('amanha') ? 'filled' : 'outlined'}
                                  sx={{ borderRadius: '999px', height: 28 }}
                                />
                              </Box>
                              <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
                                <Button size="small" variant="contained" onClick={handleSalvar} sx={{ textTransform: 'none', borderRadius: 2 }}>
                                  Salvar
                                </Button>
                                <Button size="small" onClick={resetForm} sx={{ textTransform: 'none', color: 'text.secondary' }}>
                                  Cancelar
                                </Button>
                              </Box>
                            </Box>
                          </Box>
                        ) : null}
                      </Box>

                      <Box sx={{ flex: 1, px: 2, pb: 1 }}>
                        {pendentes.length === 0 && !adding ? (
                          <Box sx={{ textAlign: 'center', py: 6, px: 2 }}>
                            <TaskAltIcon sx={{ fontSize: 56, color: 'primary.main', mb: 2 }} />
                            <Typography variant="h6" fontWeight={400} gutterBottom>
                              {filtroNav === 'compartilhadas'
                                ? 'Nenhuma tarefa compartilhada com você'
                                : concluidasCount > 0
                                  ? 'Todas as tarefas concluídas'
                                  : 'Nenhuma tarefa ainda'}
                            </Typography>
                          </Box>
                        ) : (
                          pendentes.map((t) => <TaskRow key={t.id} {...taskRowProps(t)} />)
                        )}
                      </Box>

                      {concluidasCount > 0 && (
                        <Box sx={{ borderTop: '1px solid rgba(255,255,255,0.06)', mt: 'auto' }}>
                          <ListItemButton onClick={() => setConcluidasOpen((v) => !v)} sx={{ py: 1.5, px: 2.5 }}>
                            <Typography variant="body2" color="text.secondary" sx={{ flex: 1 }}>
                              Concluída ({concluidasCount})
                            </Typography>
                            {concluidasOpen ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                          </ListItemButton>
                          <Collapse in={concluidasOpen}>
                            <Box sx={{ px: 2, pb: 2 }}>
                              {concluidas.map((t) => (
                                <TaskRow key={t.id} {...taskRowProps(t)} />
                              ))}
                            </Box>
                          </Collapse>
                        </Box>
                      )}
                    </>
                  )}
                </Box>
              </Box>
            </Box>
          )}
        </Box>
      </Drawer>

      {shareTarefa && podeCompartilhar && (
        <CompartilharDialog
          open={Boolean(shareTarefa)}
          onClose={() => setShareTarefa(null)}
          tituloItem={shareTarefa.titulo}
          tipo="tarefa"
          compartilhamentos={
            (tarefasProprias.find((t) => t.id === shareTarefa.id) || shareTarefa).compartilhamentos || []
          }
          listarUsuarios={listarUsuariosCompartilhaveis}
          onCompartilhar={(usuarioId, permissao) => compartilharTarefa(shareTarefa.id, usuarioId, permissao)}
          onAlterarPermissao={(usuarioId, permissao) =>
            atualizarCompartilhamentoTarefa(shareTarefa.id, usuarioId, permissao)
          }
          onRemover={(usuarioId) => removerCompartilhamentoTarefa(shareTarefa.id, usuarioId)}
        />
      )}
    </ThemeProvider>
  );
}
